import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { makeGetGoalsBySourceType } from 'selectors/goals';
import { makeGetFetchedStateWithoutId, makeGetLoadingStateWithoutId, makeGetErrorStateWithoutId } from 'selectors/global';

import { fetchGoals } from 'actions/goals';

import ResourceSelector from 'components/ResourceSelector';
import Select from 'components/select';

const GoalSelector = ({ label, goals, excluded, selected, projectId, fetched, loading, errored, itemComponent, useSelect, autoSelectFirst, onSelect, onResourceCreate }) => {
    const { t } = useTranslation('goal');

    const attemptFetch = useCallback(() => {
        if (!errored && !fetched && !loading) {
            fetchGoals();
        }
    }, [errored, fetched, loading]);

    useEffect(() => {
        attemptFetch();
    }, [attemptFetch]);

    const availableGoals = useMemo(() => {
        let groups;
        if (excluded.length) {
            groups = goals.filter(({ id }) => !excluded.includes(id));
        } else {
            groups = goals;
        }

        return groups;
    }, [goals, excluded]);

    const handleChange = useCallback(id => {
        onSelect(id);
    }, [onSelect]);

    useEffect(() => {
        if (autoSelectFirst && !selected && availableGoals.length) {
            onSelect(availableGoals[0].id);
        }
    }, [autoSelectFirst, selected, availableGoals]);

    if (useSelect) {
        return (
            <Select
                label={label}
                placeholder={ t('selector.title') }
                withSearch={true}
                emptyText={ t('selector.noEntries') }
                options={availableGoals.map(goal => ({ id: goal.id, text: goal.name, color: goal.color === 'transparent' ? undefined : goal.color }))}
                selected={selected}
                onChange={handleChange}
            />
        );
    }

    return (
        <ResourceSelector
            withSearch={true}
            loading={!fetched}
            searchPlaceholder={ t('selector.search') }
            emptyText={ t('selector.noEntries') }
            resourceCreateText={ t('selector.createResource') }
            items={availableGoals.map(goal => ({
                id: goal.id,
                title: goal.name
            }))}
            selected={selected}
            itemComponent={itemComponent}
            onResourceCreate={onResourceCreate}
            onSelect={handleChange}
        />
    );
}

GoalSelector.defaultProps = {
    excluded: []
}

const makeMapStateToProps = () => {
    const getGoalsBySourceType = makeGetGoalsBySourceType();
    const getLoading = makeGetLoadingStateWithoutId();
    const getFetched = makeGetFetchedStateWithoutId();
    const getErrored = makeGetErrorStateWithoutId();

    return ({ goals }, ownProps) => ({
        goals: getGoalsBySourceType(goals.items, 'manual'),
        loading: getLoading(goals.fetchInfo, 'team'),
        fetched: getFetched(goals.fetchInfo, 'team'),
        errored: getErrored(goals.fetchInfo, 'team')
    });
}

export default connect(makeMapStateToProps)(GoalSelector);
